import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';
import FullScreenLoader from './pages/FullScreenLoader';
import { clearCache } from './utils/dashboardCache';
import apiClient from './api/client';

// 1. --- Verificación de usuario mediante FastAPI ---
const verifyUserCredentials = async (identifier, password) => {
    try {
        const { data } = await apiClient.post('/usuarios/login', {
            identifier: identifier,
            password: password
        });
        return data; // FastAPI devuelve el usuario completo con allowed_paths
    } catch (e) {
        console.error("Error al autenticar:", e.response?.data?.detail || e.message);
        return null;
    }
};

// 2. Creación del Contexto
const AuthContext = createContext(null);

// 3. Creación del Proveedor (AuthProvider)
export const AuthProvider = ({ children }) => {
  const [session, setSession] = useState(null);
  const [isAuthResolved, setIsAuthResolved] = useState(false);
  const [profile, setProfile] = useState(null);
  const [globalLoading, setGlobalLoading] = useState({ active: false, message: '' });
  const [userPermissions, setUserPermissions] = useState([]);

  // Limpiar sesión al cargar (forzar logout al refrescar)
  useEffect(() => {
    try {
      sessionStorage.removeItem('dimassi.auth.session');
      setSession(null);
      setProfile(null);
      setUserPermissions([]);
    } catch (error) {
      console.error("Error resetting auth on load:", error);
      setSession(null);
    } finally {
      setIsAuthResolved(true);
    }
  }, []);

  // Función para procesar allowed_paths
  const processAllowedPaths = useCallback((paths) => {
    if (!paths) return [];
    if (Array.isArray(paths)) return paths;
    if (typeof paths === 'string') {
      try {
        const parsed = JSON.parse(paths);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }, []);

  // Función de verificación (sin login)
  const verifyUser = useCallback(async (identifier, password) => {
    const user = await verifyUserCredentials(identifier, password);
    return user ? { user, error: null } : { user: null, error: { message: "Credenciales inválidas." } };
  }, []);

  // Función de Login: guarda en sessionStorage y estado
  const login = useCallback((user) => {
    if (!user) return;
    
    console.log("🔑 Login - Usuario:", user.username);
    console.log("🔑 Login - allowed_paths crudo:", user.allowed_paths);
    
    // Procesar permisos del usuario
    let paths = processAllowedPaths(user.allowed_paths);
    
    // SIEMPRE incluir /home
    if (!paths.includes('/home')) {
      paths.unshift('/home');
    }
    
    console.log("✅ Login - Permisos procesados:", paths);
    
    const updatedUser = { ...user, allowed_paths: paths };
    const sessionData = { user: updatedUser };
    
    setSession(sessionData);
    setProfile(updatedUser);
    setUserPermissions(paths);
    
    // Cache en localStorage
    const userCache = { 
      username: user.username, 
      cargo: user.cargo,
      allowed_paths: paths 
    };
    localStorage.setItem('dimassi.user.cache', JSON.stringify(userCache));
    
    // Guardar en sessionStorage
    sessionStorage.setItem('dimassi.auth.session', JSON.stringify(sessionData));
  }, [processAllowedPaths]);

  // Función para verificar acceso a una ruta
  const hasAccess = useCallback((path) => {
    if (!profile) {
      console.log("❌ No hay perfil");
      return false;
    }
    
    console.log(`🔐 Verificando acceso a "${path}" para ${profile.username}`);
    console.log(`   Permisos:`, userPermissions);
    
    // SIEMPRE permitir /home y /configuracion
    if (path === '/home' || path === '/configuracion') {
      console.log("✅ Ruta pública (home)");
      return true;
    }
    
    // Coordinador de Inteligencia de Negocio tiene acceso total
    if (profile.cargo === 'Coordinador de Inteligencia de Negocio') {
      console.log("✅ Coordinador - acceso total");
      return true;
    }
    
    // Director General y Directora Comercial
    if (profile.cargo === 'Director General' || profile.cargo === 'Directora Comercial') {
      console.log("✅ Director - acceso total");
      return true;
    }
    
    // Verificar si tiene acceso total
    if (userPermissions.includes('*')) {
      console.log("✅ Tiene '*' - acceso total");
      return true;
    }
    
    // Verificar ruta exacta
    if (userPermissions.includes(path)) {
      console.log("✅ Coincidencia exacta");
      return true;
    }

    // Heredar acceso para Consolidación de Despacho si tiene permisos de Almacén
    if (path === '/consolidaciondespacho' && (
      userPermissions.includes('/panelalmacen') ||
      userPermissions.includes('/almacennotasentrega') ||
      userPermissions.includes('/almacen')
    )) {
      console.log("✅ Acceso heredado a Consolidación de Despacho");
      return true;
    }
    
    // Verificar con normalización (para manejar acentos)
    const normalizePath = (p) => {
      return p
        .normalize('NFC')
        .toLowerCase();
    };
    
    const normalizedPath = normalizePath(path);
    const normalizedPerms = userPermissions.map(p => normalizePath(p));
    
    if (normalizedPerms.includes(normalizedPath)) {
      console.log("✅ Coincidencia normalizada");
      return true;
    }
    
    console.log("❌ Acceso denegado");
    return false;
  }, [profile, userPermissions]);

  // Función de Logout: limpia el estado y sessionStorage
  const logout = useCallback(() => {
    clearCache();
    setSession(null);
    setProfile(null);
    setUserPermissions([]);
    sessionStorage.removeItem('dimassi.auth.session');
    localStorage.removeItem('dimassi.user.cache');
  }, []);

  const value = useMemo(() => ({
    session,
    profile,
    isAuthResolved,
    globalLoading,
    setGlobalLoading,
    verifyUser,
    login,
    logout,
    hasAccess,
    userPermissions,
  }), [session, profile, isAuthResolved, globalLoading, verifyUser, login, logout, hasAccess, userPermissions]);

  return (
    <AuthContext.Provider value={value}>
      {globalLoading.active && <FullScreenLoader message={globalLoading.message} />}
      {children}
    </AuthContext.Provider>
  );
};

// 4. Hook personalizado para usar el contexto
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth debe ser usado dentro de un AuthProvider');
    }
    return context;
};